import { useEffect, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import { formatLabel, toIsoUtc } from "../labels";
import type {
  DataClassification,
  Priority,
  SupportMessageType,
  SupportQueue,
  SupportTicketAction,
  SupportTicketLinkEntityType,
  SupportTicketLinkType,
} from "../types";

const QUEUES: SupportQueue[] = [
  "general",
  "booking",
  "payment",
  "creator",
  "business",
  "community",
  "trust_and_safety",
  "future_governed_type",
];

const PRIORITIES: Priority[] = ["low", "normal", "high", "urgent", "critical"];

const LINK_ENTITY_TYPES: SupportTicketLinkEntityType[] = [
  "booking",
  "business",
  "creator_profile",
  "community",
  "transaction",
  "moderation_case",
  "user",
  "future_governed_type",
];

const LINK_TYPES: SupportTicketLinkType[] = [
  "primary",
  "related",
  "escalation_context",
  "resolution_context",
  "future_governed_type",
];

const MESSAGE_TYPES: SupportMessageType[] = [
  "agent_message",
  "system_message",
  "internal_reference",
  "customer_message",
];

const CLASSIFICATIONS: DataClassification[] = [
  "public",
  "internal",
  "confidential",
  "strictly_confidential",
  "regulated",
];

export function CreateTicketDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    requester_user_id?: string;
    subject_user_id?: string | null;
    queue?: SupportQueue;
    priority?: Priority;
    owner_user_id?: string | null;
    market_id?: string | null;
    contact_reason?: string | null;
    sla_due_at?: string | null;
    uses_template?: boolean;
    requires_manager?: boolean;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [requesterUserId, setRequesterUserId] = useState("");
  const [subjectUserId, setSubjectUserId] = useState("");
  const [queue, setQueue] = useState<SupportQueue>("general");
  const [priority, setPriority] = useState<Priority>("normal");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [marketId, setMarketId] = useState("");
  const [contactReason, setContactReason] = useState("");
  const [slaDueAt, setSlaDueAt] = useState("");
  const [usesTemplate, setUsesTemplate] = useState(true);
  const [requiresManager, setRequiresManager] = useState(false);
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (!open) return;
    setRequesterUserId("");
    setSubjectUserId("");
    setQueue("general");
    setPriority("normal");
    setOwnerUserId("");
    setMarketId("");
    setContactReason("");
    setSlaDueAt("");
    setUsesTemplate(true);
    setRequiresManager(false);
    setApprovalId("");
  }, [open]);

  const needsApproval = requiresManager || !usesTemplate;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Support Ticket"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={needsApproval && approvalId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                ...(requesterUserId.trim()
                  ? { requester_user_id: requesterUserId.trim() }
                  : {}),
                ...(subjectUserId.trim()
                  ? { subject_user_id: subjectUserId.trim() }
                  : {}),
                queue,
                priority,
                ...(ownerUserId.trim()
                  ? { owner_user_id: ownerUserId.trim() }
                  : {}),
                ...(marketId.trim() ? { market_id: marketId.trim() } : {}),
                ...(contactReason.trim()
                  ? { contact_reason: contactReason.trim() }
                  : {}),
                ...(slaDueAt.trim()
                  ? { sla_due_at: toIsoUtc(slaDueAt) }
                  : {}),
                uses_template: usesTemplate,
                ...(requiresManager ? { requires_manager: true } : {}),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Support Tickets are independent from Moderation Cases. Link a case
          later only when authorized context requires it — workflows stay
          separate.
        </Text>
        <Input
          label="Requester User ID (optional; defaults to actor)"
          value={requesterUserId}
          onChange={(event) => setRequesterUserId(event.target.value)}
        />
        <Input
          label="Subject User ID (optional)"
          value={subjectUserId}
          onChange={(event) => setSubjectUserId(event.target.value)}
        />
        <Select
          label="Queue"
          value={queue}
          onChange={(event) => setQueue(event.target.value as SupportQueue)}
          options={QUEUES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Select
          label="Priority"
          value={priority}
          onChange={(event) => setPriority(event.target.value as Priority)}
          options={PRIORITIES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        <Input
          label="Market ID (optional)"
          value={marketId}
          onChange={(event) => setMarketId(event.target.value)}
        />
        <Textarea
          label="Contact reason"
          value={contactReason}
          onChange={(event) => setContactReason(event.target.value)}
        />
        <Input
          label="SLA due at (optional; API field only)"
          type="datetime-local"
          value={slaDueAt}
          onChange={(event) => setSlaDueAt(event.target.value)}
        />
        <Checkbox
          label="Uses approved template"
          checked={usesTemplate}
          onChange={(event) => setUsesTemplate(event.target.checked)}
        />
        <Checkbox
          label="Requires Manager Approval"
          checked={requiresManager}
          onChange={(event) => setRequiresManager(event.target.checked)}
        />
        {needsApproval ? (
          <ApprovalPickerField
            label="Manager approved Approval (required)"
            value={approvalId}
            onChange={setApprovalId}
            required
            minAuthority="manager"
            subjectType="support_ticket"
          />
        ) : null}
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function TicketActionDialog({
  open,
  actions,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  actions: SupportTicketAction[];
  onClose: () => void;
  onSubmit: (input: {
    action: SupportTicketAction;
    reason?: string;
    owner_user_id?: string;
    approval_id?: string;
    uses_template?: boolean;
    requires_manager?: boolean;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const first = actions[0] ?? "open";
  const [action, setAction] = useState<SupportTicketAction>(first);
  const [reason, setReason] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [usesTemplate, setUsesTemplate] = useState(true);
  const [requiresManager, setRequiresManager] = useState(false);
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (!open) return;
    setAction(first);
    setReason("");
    setOwnerUserId("");
    setUsesTemplate(true);
    setRequiresManager(false);
    setApprovalId("");
  }, [open, first]);

  const needsApproval = requiresManager || !usesTemplate;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Ticket status action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={
              action === "close" || action === "escalate" ? "danger" : "primary"
            }
            loading={busy}
            disabled={
              reason.trim().length < 3 ||
              (action === "assign" && ownerUserId.trim().length === 0) ||
              (needsApproval && approvalId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                action,
                reason: reason.trim(),
                ...(action === "assign" && ownerUserId.trim()
                  ? { owner_user_id: ownerUserId.trim() }
                  : {}),
                uses_template: usesTemplate,
                ...(requiresManager ? { requires_manager: true } : {}),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Status changes are audited. Escalation and resolution do not merge
          this ticket into a Moderation Case.
        </Text>
        <Select
          label="Action"
          value={action}
          onChange={(event) =>
            setAction(event.target.value as SupportTicketAction)
          }
          options={actions.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {action === "assign" ? (
          <Input
            label="Owner User ID"
            value={ownerUserId}
            onChange={(event) => setOwnerUserId(event.target.value)}
          />
        ) : null}
        <Checkbox
          label="Uses approved template / guidance"
          checked={usesTemplate}
          onChange={(event) => setUsesTemplate(event.target.checked)}
        />
        <Checkbox
          label="Requires Manager Approval"
          checked={requiresManager}
          onChange={(event) => setRequiresManager(event.target.checked)}
        />
        {needsApproval ? (
          <ApprovalPickerField
            label="Manager approved Approval"
            value={approvalId}
            onChange={setApprovalId}
            minAuthority="manager"
            subjectType="support_ticket"
          />
        ) : null}
        <Textarea
          label="Reason / rationale"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateConversationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    message_type?: SupportMessageType;
    body_reference: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [messageType, setMessageType] =
    useState<SupportMessageType>("agent_message");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!open) return;
    setMessageType("agent_message");
    setBody("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add conversation message"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={body.trim().length === 0}
            onClick={() =>
              void onSubmit({
                message_type: messageType,
                body_reference: body.trim(),
              })
            }
          >
            Confirm send record
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          External conversation is separate from Internal Notes. Creating a
          message records the communication; delivery status is updated with
          deliberate follow-up actions.
        </Text>
        <Select
          label="Message type"
          value={messageType}
          onChange={(event) =>
            setMessageType(event.target.value as SupportMessageType)
          }
          options={MESSAGE_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Textarea
          label="Body reference"
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function ConversationActionDialog({
  open,
  action,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  action: string | null;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={action ? `Delivery ${formatLabel(action)}` : "Delivery action"}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={action === "suppress" || action === "mark_failed" ? "danger" : "primary"}
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() => void onSubmit(reason.trim())}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Delivery status updates are deliberate. Do not treat a recorded
          message as delivered until marked.
        </Text>
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateLinkDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    entity_type: SupportTicketLinkEntityType;
    entity_id: string;
    link_type: SupportTicketLinkType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [entityType, setEntityType] =
    useState<SupportTicketLinkEntityType>("user");
  const [entityId, setEntityId] = useState("");
  const [linkType, setLinkType] = useState<SupportTicketLinkType>("related");

  useEffect(() => {
    if (!open) return;
    setEntityType("user");
    setEntityId("");
    setLinkType("related");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Attach linked record"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={entityId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                entity_type: entityType,
                entity_id: entityId.trim(),
                link_type: linkType,
              })
            }
          >
            Attach
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Links provide minimum-necessary context and never widen access.
          Linking a moderation_case does not merge Support into Trust &amp;
          Safety.
        </Text>
        <Select
          label="Entity type"
          value={entityType}
          onChange={(event) =>
            setEntityType(event.target.value as SupportTicketLinkEntityType)
          }
          options={LINK_ENTITY_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Entity ID"
          value={entityId}
          onChange={(event) => setEntityId(event.target.value)}
        />
        <Select
          label="Link type"
          value={linkType}
          onChange={(event) =>
            setLinkType(event.target.value as SupportTicketLinkType)
          }
          options={LINK_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Attach failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateNoteDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    body_reference: string;
    classification?: DataClassification;
    moderation_case_id?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [body, setBody] = useState("");
  const [classification, setClassification] =
    useState<DataClassification>("internal");
  const [caseId, setCaseId] = useState("");

  useEffect(() => {
    if (!open) return;
    setBody("");
    setClassification("internal");
    setCaseId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add internal note"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={body.trim().length === 0}
            onClick={() =>
              void onSubmit({
                body_reference: body.trim(),
                classification,
                ...(caseId.trim()
                  ? { moderation_case_id: caseId.trim() }
                  : {}),
              })
            }
          >
            Add note
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Internal Notes are classified and separate from customer-facing
          conversation. An optional Moderation Case ID is a link pointer only —
          not a merged workflow.
        </Text>
        <Textarea
          label="Body reference"
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        <Select
          label="Classification"
          value={classification}
          onChange={(event) =>
            setClassification(event.target.value as DataClassification)
          }
          options={CLASSIFICATIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Moderation Case ID (optional link)"
          value={caseId}
          onChange={(event) => setCaseId(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}
